import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Select,
  TextInput,
  Button,
  Group,
  Stack,
  Text,
  Checkbox,
  ScrollArea,
  Paper,
  Badge,
  Loader,
  Center,
  Alert,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconSearch, IconAlertCircle } from "@tabler/icons-react";
import { bridgeService, type Device } from "../../services/bridgeService";
import { deviceBridgeService } from "../../services/deviceBridge.service";
import {
  slideLabelPrinterService,
  type SlideLabelData,
} from "../../services/slideLabelPrinter.service";
import { cassetteLabelPrinterService } from "../../services/cassetteLabelPrinter.service";
import {
  getStickerTemplateUpdateMessage,
  isStickerTemplateVersionAtLeast,
  MIN_DYMO_SLIDE_TEMPLATE_VERSION,
  stickerLabelPrinterService,
  type StickerLabelData,
} from "../../services/stickerLabelPrinter.service";
import type { CassetteLabel } from "../accessioning/CassetteLabelPreview";
import {
  useCases,
  useCaseSpecimens,
  useCaseBlocks,
  useCaseSlides,
} from "../../hooks/useCases";
import type {
  Case,
  CaseSpecimen,
  CaseBlock,
  CaseSlide,
} from "../../services/cases.service";
import { resolvePatientLabelName } from "../../utils/patientLabelName";
import {
  getDisplayCaseNumber,
  getPhysicalSlideNumber,
} from "../../utils/caseIdentity";
import {
  formatDateOnlyForDisplay,
  parseDateOnly,
  requireDateOnlyString,
} from "../../utils/dateOnly";

interface Props {
  opened: boolean;
  onClose: () => void;
  devices: Device[];
}

type PrintCommandType =
  | "print_slide_labels"
  | "print_cassette_labels"
  | "print_stickers"
  | "test_connection"
  | "scan_document";

function getCommandTypeForDevice(
  deviceType: Device["deviceType"]
): PrintCommandType {
  switch (deviceType) {
    case "slide_printer":
      return "print_slide_labels";
    case "cassette_printer":
      return "print_cassette_labels";
    case "sticker_printer":
      return "print_stickers";
    case "document_scanner":
      return "scan_document";
    default:
      return "test_connection";
  }
}

function getCommandTypeLabel(commandType: string): string {
  switch (commandType) {
    case "print_slide_labels":
      return "Print Slide Labels";
    case "print_cassette_labels":
      return "Print Cassette Labels";
    case "print_stickers":
      return "Print Stickers";
    case "scan_document":
      return "Scan Document";
    case "test_connection":
      return "Test Connection";
    default:
      return commandType;
  }
}

interface CommandDispatchSlideLabelsInput {
  blocks: CaseBlock[];
  caseData: Case;
  selectedSlideIds: Set<string>;
  slides: CaseSlide[];
  specimens: CaseSpecimen[];
}

export function buildCommandDispatchSlideLabels({
  blocks,
  caseData,
  selectedSlideIds,
  slides,
  specimens,
}: CommandDispatchSlideLabelsInput): SlideLabelData[] {
  const displayCaseNumber = getDisplayCaseNumber(caseData);
  const patientName =
    resolvePatientLabelName(caseData.patient, caseData.draftPatientData) ?? "";
  const patientDOB = caseData.patient?.dateOfBirth
    ? requireDateOnlyString(
        caseData.patient.dateOfBirth,
        "Patient date of birth"
      )
    : undefined;
  const dateReceived = requireDateOnlyString(
    caseData.dateReceived,
    "Date received"
  );

  return slides
    .filter((slide) => selectedSlideIds.has(slide.id))
    .map((slide) => {
      const block = blocks.find((candidate) => candidate.id === slide.blockId);
      const specimen = specimens.find(
        (candidate) => candidate.id === slide.specimenId
      );
      const slideNumber = getPhysicalSlideNumber(slide);
      return {
        caseNumber: displayCaseNumber,
        patientName,
        patientDOB,
        specimenLetter: specimen?.number ?? "",
        blockNumber: block?.number ?? "",
        slideNumber,
        stainCode: slide.stain?.code ?? "H&E",
        barcode: `${displayCaseNumber};${specimen?.number ?? ""};${
          block?.number ?? ""
        };${slideNumber}`,
        dateReceived,
      };
    });
}

interface CommandDispatchStickerLabelsInput {
  blocks: CaseBlock[];
  caseData: Case;
  patientName: string;
  selectedBlocks: CaseBlock[];
  selectedSlides: CaseSlide[];
  specimens: CaseSpecimen[];
}

export function buildCommandDispatchStickerLabels({
  blocks,
  caseData,
  patientName,
  selectedBlocks,
  selectedSlides,
  specimens,
}: CommandDispatchStickerLabelsInput): StickerLabelData[] {
  const dateReceived = requireDateOnlyString(
    caseData.dateReceived,
    "Date received"
  );
  const displayCaseNumber = getDisplayCaseNumber(caseData);

  return [
    ...selectedBlocks.map((block) => {
      const specimen = specimens.find(
        (candidate) => candidate.id === block.specimenId
      );
      const specimenNumber = specimen?.number ?? "";
      const blockNumber = block.number ?? "";
      return {
        itemSlotId: `${Date.now()}-b-${block.id}`,
        itemType: "block" as const,
        caseId: caseData.id,
        itemId: block.id,
        caseNumber: displayCaseNumber,
        patientName,
        specimen: specimenNumber,
        blockId: blockNumber,
        slideId: "",
        stain: "",
        dateReceived,
        barcodeValue: `${displayCaseNumber};${specimenNumber};${blockNumber}`,
      };
    }),
    ...selectedSlides.map((slide) => {
      const block = blocks.find((candidate) => candidate.id === slide.blockId);
      const specimen = specimens.find(
        (candidate) => candidate.id === slide.specimenId
      );
      const specimenNumber = specimen?.number ?? "";
      const blockNumber = block?.number ?? "";
      const slideNumber = getPhysicalSlideNumber(slide);
      return {
        itemSlotId: `${Date.now()}-s-${slide.id}`,
        itemType: "slide" as const,
        caseId: caseData.id,
        itemId: slide.id,
        caseNumber: displayCaseNumber,
        patientName,
        specimen: specimenNumber,
        blockId: blockNumber,
        slideId: slideNumber,
        stain: slide.stain?.code ?? "",
        dateReceived,
        barcodeValue: `${displayCaseNumber};${specimenNumber};${blockNumber};${slideNumber}`,
      };
    }),
  ];
}

export function formatCommandDispatchCaseDateReceived(value: unknown): string {
  const dateReceived = parseDateOnly(value);
  return dateReceived ? formatDateOnlyForDisplay(dateReceived) : "N/A";
}

const CommandDispatchModal: React.FC<Props> = ({
  opened,
  onClose,
  devices,
}) => {
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [caseSearch, setCaseSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(caseSearch, 400);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [selectedSlideIds, setSelectedSlideIds] = useState<Set<string>>(
    new Set()
  );
  const [selectedBlockIds, setSelectedBlockIds] = useState<Set<string>>(
    new Set()
  );

  // Derive command type from selected device
  const selectedDevice = devices.find((d) => d.id === deviceId) || null;
  const commandType = selectedDevice
    ? getCommandTypeForDevice(selectedDevice.deviceType)
    : null;

  const isPrintCommand =
    commandType === "print_slide_labels" ||
    commandType === "print_cassette_labels" ||
    commandType === "print_stickers";

  // Search cases
  const { data: casesData, isLoading: casesLoading } = useCases(
    { search: debouncedSearch },
    1,
    10
  );
  const cases = useMemo(
    () => (debouncedSearch ? casesData?.data ?? [] : []),
    [casesData, debouncedSearch]
  );

  // Fetch specimens, blocks, slides for selected case
  const { data: specimens } = useCaseSpecimens(selectedCaseId || undefined);
  const { data: blocks } = useCaseBlocks(selectedCaseId || undefined);
  const { data: slides } = useCaseSlides(selectedCaseId || undefined);

  const selectedCase = cases.find((c: Case) => c.id === selectedCaseId);

  // Reset selections when device or case changes
  useEffect(() => {
    setSelectedSlideIds(new Set());
    setSelectedBlockIds(new Set());
  }, [selectedCaseId, commandType]);

  // Reset everything when modal closes
  useEffect(() => {
    if (!opened) {
      setDeviceId(null);
      setCaseSearch("");
      setSelectedCaseId(null);
      setSelectedSlideIds(new Set());
      setSelectedBlockIds(new Set());
    }
  }, [opened]);

  const toggleSlide = (slideId: string) => {
    setSelectedSlideIds((prev) => {
      const next = new Set(prev);
      if (next.has(slideId)) next.delete(slideId);
      else next.add(slideId);
      return next;
    });
  };

  const toggleBlock = (blockId: string) => {
    setSelectedBlockIds((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  };

  const selectAllSlides = () => {
    if (slides) {
      setSelectedSlideIds(new Set(slides.map((s: CaseSlide) => s.id)));
    }
  };

  const selectAllBlocks = () => {
    if (blocks) {
      setSelectedBlockIds(new Set(blocks.map((b: CaseBlock) => b.id)));
    }
  };

  const sendCommand = async (
    devId: string,
    cmdType: string,
    payload: Record<string, unknown>
  ) => {
    setLoading(true);
    try {
      // Try local bridge first
      if (deviceBridgeService.isLocal()) {
        try {
          const result = await deviceBridgeService.sendCommand(
            { type: cmdType, payload: { ...payload, deviceId: devId } },
            30000
          );
          const p = result.payload ?? {};
          if (p.success) {
            notifications.show({
              title: "Command Sent",
              message: `${getCommandTypeLabel(
                cmdType
              )} completed via local bridge`,
              color: "green",
            });
            onClose();
            return;
          }
          notifications.show({
            title: "Command Failed",
            message: (p.error as string) || "Unknown error",
            color: "red",
          });
          return;
        } catch {
          // Fall through to backend
        }
      }

      // Backend fallback
      await bridgeService.createCommand({
        deviceId: devId,
        commandType: cmdType,
        payload,
      });
      notifications.show({
        title: "Command Sent",
        message: `${getCommandTypeLabel(cmdType)} dispatched via backend`,
        color: "green",
      });
      onClose();
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.response?.data?.message || "Failed to send command",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const sendSlidePrint = async (caseData: Case, devId: string) => {
    if (!slides || !specimens || !blocks) return;
    if (
      !parseDateOnly(caseData.dateReceived) ||
      (caseData.patient?.dateOfBirth &&
        !parseDateOnly(caseData.patient.dateOfBirth))
    ) {
      notifications.show({
        title: "Print Failed",
        message:
          "The case has a missing or invalid Date Received or patient date of birth.",
        color: "red",
      });
      return;
    }

    // Pass the explicitly selected device so the service routes to that exact
    // printer through the backend, independent of saved preferences.
    setLoading(true);
    try {
      const labelData = buildCommandDispatchSlideLabels({
        blocks,
        caseData,
        selectedSlideIds,
        slides,
        specimens,
      });
      const result = await slideLabelPrinterService.printSlideLabels(
        labelData,
        devId
      );

      if (result.success) {
        notifications.show({
          title: "Print Sent",
          message: `${result.count} slide label(s) sent to printer`,
          color: "green",
        });
        onClose();
      } else {
        notifications.show({
          title: "Print Failed",
          message: result.error || "Unknown error",
          color: "red",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const sendStickerPrint = async (caseData: Case, devId: string) => {
    if (!slides || !blocks || !specimens) return;
    if (!parseDateOnly(caseData.dateReceived)) {
      notifications.show({
        title: "Print Failed",
        message: "The case has a missing or invalid Date Received.",
        color: "red",
      });
      return;
    }

    const device = devices.find((d) => d.id === devId);
    if (!device) return;
    const workstationId = device.workstationId;

    const selectedSlides = slides.filter(
      (s: CaseSlide) => selectedSlideIds.has(s.id) && s.isCompleted
    );
    const selectedBlocks = blocks.filter(
      (b: CaseBlock) => selectedBlockIds.has(b.id) && !!b.embeddedAt
    );

    if (selectedSlides.length === 0 && selectedBlocks.length === 0) {
      notifications.show({
        title: "Nothing to print",
        message:
          "Sticker prints require completed slides or blocks. Mark items as completed first.",
        color: "orange",
      });
      return;
    }

    if (
      selectedSlides.length > 0 &&
      !isStickerTemplateVersionAtLeast(
        device.metadata?.stickerTemplateVersion,
        MIN_DYMO_SLIDE_TEMPLATE_VERSION
      )
    ) {
      notifications.show({
        title: "Bridge Update Required",
        message: getStickerTemplateUpdateMessage(
          MIN_DYMO_SLIDE_TEMPLATE_VERSION,
          device.metadata?.stickerTemplateVersion
        ),
        color: "red",
      });
      return;
    }

    const patientName =
      resolvePatientLabelName(caseData.patient, caseData.draftPatientData) ??
      "";

    setLoading(true);
    try {
      const labels = buildCommandDispatchStickerLabels({
        blocks,
        caseData,
        patientName,
        selectedBlocks,
        selectedSlides,
        specimens,
      });
      const result = await stickerLabelPrinterService.printStickerLabels(
        labels,
        devId,
        workstationId,
        device.metadata?.stickerTemplateVersion
      );

      if (result.success) {
        notifications.show({
          title: "Stickers Dispatched",
          message: `${result.count} sticker label(s) sent via ${result.method} bridge`,
          color: "green",
        });
        onClose();
      } else {
        notifications.show({
          title: "Print Failed",
          message: result.error || "Sticker print failed",
          color: "red",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const sendCassettePrint = async (caseData: Case, devId: string) => {
    if (!blocks || !specimens) return;
    const displayCaseNumber = getDisplayCaseNumber(caseData);

    const patientName = caseData.patient
      ? `${caseData.patient.lastName}, ${caseData.patient.firstName}`
      : "Unknown";
    const facilityName = caseData.facility?.name ?? "Gulf Coast Pathologists";

    const labelData: CassetteLabel[] = blocks
      .filter((b: CaseBlock) => selectedBlockIds.has(b.id))
      .map((block: CaseBlock) => {
        const specimen = specimens.find(
          (sp: CaseSpecimen) => sp.id === block.specimenId
        );
        return {
          blockId: block.id,
          barcode: `${displayCaseNumber};${specimen?.number ?? ""};${
            block.number
          }`,
          caseNumber: displayCaseNumber,
          specimenLetter: specimen?.number ?? "",
          blockNumber: block.number,
          patientName,
          facilityName,
        };
      });

    setLoading(true);
    try {
      const result = await cassetteLabelPrinterService.printCassetteLabels(
        labelData,
        devId
      );

      if (result.success) {
        notifications.show({
          title: "Print Sent",
          message: `${result.count} cassette label(s) sent to printer`,
          color: "green",
        });
        onClose();
      } else {
        notifications.show({
          title: "Print Failed",
          message: result.error || "Unknown error",
          color: "red",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!deviceId || !commandType) {
      notifications.show({
        title: "Select a device",
        message: "Please select a device to send a command to",
        color: "orange",
      });
      return;
    }

    // Test connection — no case needed
    if (commandType === "test_connection") {
      await sendCommand(deviceId, commandType, {});
      return;
    }

    // Scan document — no case needed
    if (commandType === "scan_document") {
      await sendCommand(deviceId, commandType, {
        format: "pdf",
        colorMode: "color",
        resolution: 300,
      });
      return;
    }

    if (!selectedCase) {
      notifications.show({
        title: "Select a case",
        message: "Search and select a case to generate labels from",
        color: "orange",
      });
      return;
    }

    // Build print payload from selected case data
    if (commandType === "print_slide_labels") {
      if (selectedSlideIds.size === 0) {
        notifications.show({
          title: "Select slides",
          message: "Please select at least one slide to print",
          color: "orange",
        });
        return;
      }
      await sendSlidePrint(selectedCase, deviceId);
    } else if (commandType === "print_cassette_labels") {
      if (selectedBlockIds.size === 0) {
        notifications.show({
          title: "Select blocks",
          message: "Please select at least one block to print",
          color: "orange",
        });
        return;
      }
      await sendCassettePrint(selectedCase, deviceId);
    } else if (commandType === "print_stickers") {
      if (selectedSlideIds.size === 0 && selectedBlockIds.size === 0) {
        notifications.show({
          title: "Select items",
          message:
            "Please select at least one completed slide or block to print stickers for",
          color: "orange",
        });
        return;
      }
      await sendStickerPrint(selectedCase, deviceId);
    }
  };

  const connectionMode = deviceBridgeService.getConnectionMode();

  return (
    <Modal opened={opened} onClose={onClose} title="Dispatch Command" size="lg">
      <Stack>
        <Group gap="xs">
          <Badge
            size="xs"
            color={
              connectionMode === "local"
                ? "green"
                : connectionMode === "backend"
                ? "yellow"
                : "gray"
            }
          >
            {connectionMode === "local"
              ? "Local Bridge"
              : connectionMode === "backend"
              ? "Backend Relay"
              : "Disconnected"}
          </Badge>
        </Group>

        <Select
          label="Device"
          placeholder="Select device"
          data={devices.map((d) => ({
            value: d.id,
            label: `${d.name} (${d.deviceType})`,
          }))}
          value={deviceId}
          onChange={setDeviceId}
          required
        />

        {commandType && (
          <Group gap="xs">
            <Text size="sm" fw={500}>
              Command:
            </Text>
            <Text size="sm" c="dimmed">
              {getCommandTypeLabel(commandType)}
            </Text>
          </Group>
        )}

        {/* Case search for print commands */}
        {isPrintCommand && (
          <>
            <TextInput
              label="Search Case"
              placeholder="Type case number or patient name..."
              leftSection={<IconSearch size={16} />}
              value={caseSearch}
              onChange={(e) => {
                setCaseSearch(e.currentTarget.value);
                setSelectedCaseId(null);
              }}
            />

            {casesLoading && debouncedSearch && (
              <Center py="xs">
                <Loader size="sm" />
              </Center>
            )}

            {!selectedCaseId && cases.length > 0 && (
              <ScrollArea.Autosize mah={200}>
                <Stack gap="xs">
                  {cases.map((c: Case) => (
                    <Paper
                      key={c.id}
                      p="xs"
                      withBorder
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setSelectedCaseId(c.id);
                        setCaseSearch(c.caseNumber);
                      }}
                    >
                      <Group justify="space-between">
                        <Text size="sm" fw={500}>
                          {c.caseNumber}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {c.patient
                            ? `${c.patient.lastName}, ${c.patient.firstName}`
                            : "No patient"}
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed">
                        {c.facility?.name} | Received:{" "}
                        {formatCommandDispatchCaseDateReceived(c.dateReceived)}
                      </Text>
                    </Paper>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            )}

            {debouncedSearch && !casesLoading && cases.length === 0 && (
              <Text size="sm" c="dimmed" ta="center">
                No cases found
              </Text>
            )}

            {/* Slide selection for slide printers */}
            {selectedCaseId && commandType === "print_slide_labels" && (
              <>
                {slides && slides.length > 0 ? (
                  <>
                    <Group justify="space-between">
                      <Text size="sm" fw={500}>
                        Select Slides ({selectedSlideIds.size}/{slides.length})
                      </Text>
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={selectAllSlides}
                      >
                        Select All
                      </Button>
                    </Group>
                    <ScrollArea.Autosize mah={250}>
                      <Stack gap={4}>
                        {slides.map((slide: CaseSlide) => {
                          const block = blocks?.find(
                            (b: CaseBlock) => b.id === slide.blockId
                          );
                          const specimen = specimens?.find(
                            (sp: CaseSpecimen) => sp.id === slide.specimenId
                          );
                          return (
                            <Checkbox
                              key={slide.id}
                              checked={selectedSlideIds.has(slide.id)}
                              onChange={() => toggleSlide(slide.id)}
                              label={
                                <Text size="sm">
                                  {specimen?.number ?? "?"}-
                                  {block?.number ?? "?"}-
                                  {getPhysicalSlideNumber(slide)}{" "}
                                  <Text span c="dimmed" size="xs">
                                    ({slide.stain?.code ?? "No stain"})
                                  </Text>
                                </Text>
                              }
                            />
                          );
                        })}
                      </Stack>
                    </ScrollArea.Autosize>
                  </>
                ) : (
                  <Alert icon={<IconAlertCircle size={16} />} color="orange">
                    No slides found for this case
                  </Alert>
                )}
              </>
            )}

            {/* Combined block + slide selection for sticker printers (completed-only) */}
            {selectedCaseId && commandType === "print_stickers" && (
              <>
                {(() => {
                  const completedBlocks =
                    blocks?.filter((b: CaseBlock) => !!b.embeddedAt) ?? [];
                  const completedSlides =
                    slides?.filter((s: CaseSlide) => s.isCompleted) ?? [];
                  return (
                    <>
                      {completedBlocks.length > 0 ? (
                        <>
                          <Group justify="space-between">
                            <Text size="sm" fw={500}>
                              Completed Blocks ({selectedBlockIds.size}/
                              {completedBlocks.length})
                            </Text>
                            <Button
                              size="xs"
                              variant="subtle"
                              onClick={() =>
                                setSelectedBlockIds(
                                  new Set(
                                    completedBlocks.map((b: CaseBlock) => b.id)
                                  )
                                )
                              }
                            >
                              Select All
                            </Button>
                          </Group>
                          <ScrollArea.Autosize mah={150}>
                            <Stack gap={4}>
                              {completedBlocks.map((block: CaseBlock) => {
                                const specimen = specimens?.find(
                                  (sp: CaseSpecimen) =>
                                    sp.id === block.specimenId
                                );
                                return (
                                  <Checkbox
                                    key={block.id}
                                    checked={selectedBlockIds.has(block.id)}
                                    onChange={() => toggleBlock(block.id)}
                                    label={
                                      <Text size="sm">
                                        {specimen?.number ?? "?"}-{block.number}
                                      </Text>
                                    }
                                  />
                                );
                              })}
                            </Stack>
                          </ScrollArea.Autosize>
                        </>
                      ) : null}
                      {completedSlides.length > 0 ? (
                        <>
                          <Group justify="space-between">
                            <Text size="sm" fw={500}>
                              Completed Slides ({selectedSlideIds.size}/
                              {completedSlides.length})
                            </Text>
                            <Button
                              size="xs"
                              variant="subtle"
                              onClick={() =>
                                setSelectedSlideIds(
                                  new Set(
                                    completedSlides.map((s: CaseSlide) => s.id)
                                  )
                                )
                              }
                            >
                              Select All
                            </Button>
                          </Group>
                          <ScrollArea.Autosize mah={150}>
                            <Stack gap={4}>
                              {completedSlides.map((slide: CaseSlide) => {
                                const block = blocks?.find(
                                  (b: CaseBlock) => b.id === slide.blockId
                                );
                                const specimen = specimens?.find(
                                  (sp: CaseSpecimen) =>
                                    sp.id === slide.specimenId
                                );
                                return (
                                  <Checkbox
                                    key={slide.id}
                                    checked={selectedSlideIds.has(slide.id)}
                                    onChange={() => toggleSlide(slide.id)}
                                    label={
                                      <Text size="sm">
                                        {specimen?.number ?? "?"}-
                                        {block?.number ?? "?"}-
                                        {getPhysicalSlideNumber(slide)}{" "}
                                        <Text span c="dimmed" size="xs">
                                          ({slide.stain?.code ?? "No stain"})
                                        </Text>
                                      </Text>
                                    }
                                  />
                                );
                              })}
                            </Stack>
                          </ScrollArea.Autosize>
                        </>
                      ) : null}
                      {completedBlocks.length === 0 &&
                        completedSlides.length === 0 && (
                          <Alert
                            icon={<IconAlertCircle size={16} />}
                            color="orange"
                          >
                            No completed blocks or slides on this case. Stickers
                            can only be printed for items marked completed.
                          </Alert>
                        )}
                    </>
                  );
                })()}
              </>
            )}

            {/* Block selection for cassette printers */}
            {selectedCaseId && commandType === "print_cassette_labels" && (
              <>
                {blocks && blocks.length > 0 ? (
                  <>
                    <Group justify="space-between">
                      <Text size="sm" fw={500}>
                        Select Blocks ({selectedBlockIds.size}/{blocks.length})
                      </Text>
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={selectAllBlocks}
                      >
                        Select All
                      </Button>
                    </Group>
                    <ScrollArea.Autosize mah={250}>
                      <Stack gap={4}>
                        {blocks.map((block: CaseBlock) => {
                          const specimen = specimens?.find(
                            (sp: CaseSpecimen) => sp.id === block.specimenId
                          );
                          return (
                            <Checkbox
                              key={block.id}
                              checked={selectedBlockIds.has(block.id)}
                              onChange={() => toggleBlock(block.id)}
                              label={
                                <Text size="sm">
                                  {specimen?.number ?? "?"}-{block.number}
                                  {block.isAdditional && (
                                    <Badge size="xs" ml={4}>
                                      Additional
                                    </Badge>
                                  )}
                                </Text>
                              }
                            />
                          );
                        })}
                      </Stack>
                    </ScrollArea.Autosize>
                  </>
                ) : (
                  <Alert icon={<IconAlertCircle size={16} />} color="orange">
                    No blocks found for this case
                  </Alert>
                )}
              </>
            )}
          </>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSend} loading={loading}>
            Send Command
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CommandDispatchModal;
